package com.schoolconnect.app.feature.common

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import com.schoolconnect.app.designsystem.AppDropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.getValue
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.dp
import com.schoolconnect.app.designsystem.AppConfirmationDialog
import com.schoolconnect.app.designsystem.AppConfirmationDialogTone
import com.schoolconnect.app.datasource.network.AuthSessionStore
import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.datasource.network.response.resolveRoleCode
import com.schoolconnect.app.domain.SchoolKeys
import com.schoolconnect.app.repository.profile.ProfileRepository
import com.schoolconnect.app.repository.schoolstaff.SchoolStaffRepository
import com.schoolconnect.app.repository.user.UserRepository
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.theme.interBold
import com.schoolconnect.app.theme.interSemiBold
import com.schoolconnect.app.util.decodeImageBitmap
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.bell
import ischoolconnect.sharedui.generated.resources.chevron_down
import ischoolconnect.sharedui.generated.resources.ic_rotate_right
import ischoolconnect.sharedui.generated.resources.logout
import ischoolconnect.sharedui.generated.resources.user
import org.jetbrains.compose.resources.painterResource
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

private val ShellBlue = Color(0xFF1F5BFF)
private val ShellNavy = Color(0xFF0F1B57)
private val ShellBorder = Color(0xFFE3E9F5)
private val ShellMenuSurface = Color.White
private val ShellMenuShadow = Color(0x1A0F1B57)

val LocalProfileAction = staticCompositionLocalOf<() -> Unit> { {} }

@Composable
fun ShellTopRightActions(
    roleLabel: String,
    roleSubtitle: String,
    initials: String,
    expanded: Boolean,
    onExpandedChange: (Boolean) -> Unit,
    onChangeRole: () -> Unit,
    onLogout: () -> Unit,
    onMyProfile: (() -> Unit)? = null,
    profileName: String = "",
    profileEmail: String = "",
    onNotificationsClick: () -> Unit = {},
    // Reserved for the future notification feed; no badge is shown until it is backed by real data.
    notificationCount: Int = 0,
    forceCompact: Boolean = false,
    modifier: Modifier = Modifier
) {
    var showLogoutDialog by remember { mutableStateOf(false) }
    val sharedProfile by ShellProfileStore.profile.collectAsState()
    var resolvedProfileName by remember(profileName, roleLabel, sharedProfile.fullName) {
        mutableStateOf(profileName.trim().ifBlank { sharedProfile.fullName.trim() })
    }
    val currentUserId = UserSessionStore.getUserId()
        .ifBlank { AuthSessionStore.getSession()?.response?.userId.orEmpty() }
    LaunchedEffect(profileName, roleLabel, currentUserId, sharedProfile.fullName) {
        if (profileName.isBlank() && sharedProfile.fullName.isBlank()) {
            val genericName = if (currentUserId.isNotBlank()) {
                UserRepository.loadUserDisplayName(currentUserId).getOrNull().orEmpty()
            } else {
                ""
            }
            val roleProfileName = if (genericName.isBlank()) {
                when (roleLabel) {
                    "Parent" -> ProfileRepository.loadParentProfile().getOrNull()?.name.orEmpty()
                    "Teacher" -> {
                        val context = AuthSessionStore.primaryContext()
                        val teacherProfile = AuthSessionStore.getSession()?.response?.identityContextProfiles
                            .orEmpty()
                            .firstOrNull { profile ->
                                profile.resolveRoleCode() == SchoolKeys.V_ROLE_TEACHER &&
                                    profile.staffId.orEmpty().isNotBlank()
                            }
                        val teacherSchoolId = teacherProfile?.schoolId.orEmpty().ifBlank { context?.schoolId.orEmpty() }
                        if (currentUserId.isNotBlank() && teacherSchoolId.isNotBlank()) {
                            SchoolStaffRepository.loadTeacherSchoolStaffProfile(
                                schoolId = teacherSchoolId,
                                userId = currentUserId,
                                preferredStaffId = teacherProfile?.staffId.orEmpty()
                            ).getOrNull()?.name.orEmpty()
                        } else {
                            ""
                        }
                    }
                    "School Admin", "Support Staff" ->
                        ProfileRepository.loadSchoolStaffProfile().getOrNull()?.name.orEmpty()
                    else -> ProfileRepository.loadAppStaffProfile().getOrNull()?.name.orEmpty()
                }
            } else {
                ""
            }
            resolvedProfileName = genericName.ifBlank { roleProfileName }.trim()
        } else if (profileName.isBlank()) {
            resolvedProfileName = sharedProfile.fullName.trim()
        }
    }
    val displayName = resolvedProfileName.ifBlank { roleLabel }
    val displayEmail = profileEmail.ifBlank { sharedProfile.email }
    val displayInitials = shellAvatarInitials(displayName)
    val profileImageUrl = sharedProfile.profileImageUrl.trim()
    var profileImage by remember(profileImageUrl) { mutableStateOf<ImageBitmap?>(null) }
    LaunchedEffect(profileImageUrl) {
        profileImage = null
        if (profileImageUrl.isNotBlank()) {
            FileUploadRepository.downloadBytes(profileImageUrl)
                .onSuccess { bytes -> profileImage = decodeImageBitmap(bytes) }
        }
    }
    LaunchedEffect(displayName, displayEmail) {
        if (displayName.isNotBlank() || displayEmail.isNotBlank()) {
            ShellProfileStore.update(displayName, displayEmail)
        }
    }
    val actionScope = rememberCoroutineScope()
    val localParentContext = LocalParentChildContext.current
    val globalChildren by ParentChildSelectionStore.children.collectAsState()
    val globalSelectedChildId by ParentChildSelectionStore.selectedChildId.collectAsState()
    // Parent child context is session/role scoped. Never fall back to the
    // global parent store for staff/admin shells after a parent logout or role
    // switch, otherwise the previous parent's selector leaks into the shell.
    val parentContext = if (roleLabel == "Parent" && localParentContext.children.isNotEmpty()) {
        localParentContext
    } else if (roleLabel == "Parent") {
        ParentChildContext(
            children = globalChildren,
            selectedChildId = globalSelectedChildId,
            onSelected = ParentChildSelectionStore::select
        )
    } else {
        ParentChildContext()
    }
    val isLogoutInProgress = LocalAppLogoutInProgress.current
    val profileAction = onMyProfile ?: LocalProfileAction.current
    BoxWithConstraints(modifier = modifier) {
        val compact = forceCompact || maxWidth < 620.dp

        Box {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(if (compact) 10.dp else 14.dp)
            ) {
                Box(
                    modifier = Modifier.size(if (compact) 38.dp else 40.dp),
                    contentAlignment = Alignment.Center
                ) {
                    IconButton(
                        onClick = onNotificationsClick,
                        modifier = Modifier.size(if (compact) 38.dp else 40.dp)
                    ) {
                        Icon(
                            painter = painterResource(Res.drawable.bell),
                            contentDescription = "Notifications",
                            tint = ShellNavy,
                            modifier = Modifier.size(if (compact) 22.dp else 24.dp)
                        )
                    }
                }

                Box(
                    modifier = Modifier
                        .width(1.dp)
                        .size(width = 1.dp, height = if (compact) 28.dp else 34.dp)
                        .background(ShellBorder)
                )

                // Parent child context is presented in the left header line;
                // keep the right side focused on notifications and account actions.
                if (roleLabel != "Parent" && parentContext.children.isNotEmpty()) {
                    ParentChildHeaderSelector(
                        context = parentContext,
                        compact = compact
                    )

                    Box(
                        modifier = Modifier
                            .width(1.dp)
                            .size(width = 1.dp, height = if (compact) 28.dp else 34.dp)
                            .background(ShellBorder)
                    )
                }

                Box {
                    Row(
                        modifier = Modifier
                            .clip(RoundedCornerShape(22.dp))
                            .clickable { onExpandedChange(!expanded) }
                            .padding(
                                horizontal = if (compact) 6.dp else 8.dp,
                                vertical = if (compact) 6.dp else 8.dp
                            ),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(if (compact) 8.dp else 12.dp)
                    ) {
                        AvatarChip(
                            initials = displayInitials,
                            image = profileImage,
                            compact = compact
                        )
                        if (!compact) {
                            Column(
                                modifier = Modifier.widthIn(max = 280.dp),
                                verticalArrangement = Arrangement.spacedBy(2.dp)
                            ) {
                                Text(
                                    text = displayName,
                                    style = MaterialTheme.typography.titleMedium,
                                    color = ShellNavy,
                                    fontFamily = interBold(),
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis
                                )
                                Text(
                                    text = roleSubtitle,
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = ShellNavy.copy(alpha = 0.72f),
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis
                                )
                            }
                        }
                        Icon(
                            painter = painterResource(Res.drawable.chevron_down),
                            contentDescription = if (expanded) "Close account menu" else "Open account menu",
                            tint = ShellNavy,
                            modifier = Modifier.size(if (compact) 18.dp else 20.dp)
                        )
                    }

                    // Keep the popup anchored to the account control, not the
                    // notification + account group that contains it.
                    ShellAccountMenu(
                        expanded = expanded,
                        onDismiss = { onExpandedChange(false) },
                        profileName = displayName,
                        profileRole = roleSubtitle,
                        profileEmail = displayEmail,
                        profileImage = profileImage,
                        onMyProfile = profileAction,
                        actionScope = actionScope,
                        onChangeRole = onChangeRole,
                        onLogout = {
                            onExpandedChange(false)
                            showLogoutDialog = true
                        }
                    )
                }
            }
        }

        if (showLogoutDialog) {
            AppConfirmationDialog(
                onDismiss = { showLogoutDialog = false },
                title = "Sign out",
                subtitle = "End your current session",
                bodyText = "Are you sure you want to sign out?",
                confirmLabel = "Sign Out",
                onConfirm = {
                    showLogoutDialog = false
                    onLogout()
                },
                tone = AppConfirmationDialogTone.Danger,
                dismissEnabled = !isLogoutInProgress,
                confirmEnabled = !isLogoutInProgress
            )
        }
    }
}

@Composable
internal fun ParentChildHeaderSelector(
    context: ParentChildContext,
    compact: Boolean,
    showAvatar: Boolean = true,
    showCurrentChildLabel: Boolean = true
) {
    var expanded by remember { mutableStateOf(false) }
    val selected = context.children.firstOrNull { it.id == context.selectedChildId }
        ?: context.children.firstOrNull()

    Box {
        Row(
            modifier = Modifier
                .clip(RoundedCornerShape(24.dp))
                .clickable { expanded = !expanded }
                .padding(
                    horizontal = if (!showAvatar) 0.dp else if (compact) 4.dp else 7.dp,
                    vertical = if (!showAvatar) 0.dp else if (compact) 5.dp else 7.dp
                ),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(if (compact) 7.dp else 10.dp)
        ) {
            if (showAvatar) {
                AvatarChip(
                    initials = selected?.name.orEmpty().split(" ").filter(String::isNotBlank).take(2).joinToString("") { it.first().uppercase() }.ifBlank { "CH" },
                    compact = compact
                )
            }
            if (!compact) {
                if (showCurrentChildLabel) {
                    Column(modifier = Modifier.widthIn(max = 220.dp), verticalArrangement = Arrangement.spacedBy(1.dp)) {
                        Text("Current child", style = MaterialTheme.typography.labelMedium, color = ShellNavy.copy(alpha = 0.68f), maxLines = 1)
                        Text(
                            text = selected?.let { "${it.name} · ${it.classLabel}" } ?: "Select child",
                            style = MaterialTheme.typography.titleMedium,
                            color = ShellNavy,
                            fontFamily = interBold(),
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                } else {
                    Text(
                        text = selected?.let { "${it.name} – Class ${it.classLabel}" } ?: "Select child",
                        style = MaterialTheme.typography.bodyMedium,
                        color = ShellBlue,
                        fontFamily = interBold(),
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }
            Icon(painterResource(Res.drawable.chevron_down), if (expanded) "Close child selector" else "Select child", tint = ShellBlue, modifier = Modifier.size(if (compact) 18.dp else 20.dp))
        }

        AppDropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            offset = DpOffset(0.dp, 8.dp),
            shape = RoundedCornerShape(18.dp),
            containerColor = Color.White,
            tonalElevation = 0.dp,
            shadowElevation = 20.dp,
            modifier = Modifier.width(340.dp).border(1.dp, ShellBorder, RoundedCornerShape(18.dp))
        ) {
            Column(modifier = Modifier.padding(vertical = 8.dp)) {
                Text("My Children", fontFamily = interBold(), color = ShellNavy, style = MaterialTheme.typography.titleLarge, modifier = Modifier.padding(horizontal = 18.dp, vertical = 8.dp))
                Text("Select a child to view their dashboard", color = ShellNavy.copy(alpha = 0.68f), style = MaterialTheme.typography.bodyMedium, modifier = Modifier.padding(horizontal = 18.dp, vertical = 2.dp))
                androidx.compose.material3.HorizontalDivider(color = ShellBorder, modifier = Modifier.padding(vertical = 8.dp))
                context.children.forEach { child ->
                    val isSelected = child.id == selected?.id
                    DropdownMenuItem(
                        text = {
                            Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
                                Text("${child.name} (${child.classLabel})", color = ShellNavy, fontFamily = interBold())
                                Text("Class ${child.classLabel}", color = ShellNavy.copy(alpha = 0.68f), style = MaterialTheme.typography.bodySmall)
                            }
                        },
                        leadingIcon = { AvatarChip(initials = child.name.split(" ").filter(String::isNotBlank).take(2).joinToString("") { it.first().uppercase() }, compact = true) },
                        trailingIcon = if (isSelected) ({ Text("Selected", color = ShellBlue, fontFamily = interBold()) }) else null,
                        onClick = { expanded = false; context.onSelected(child.id) },
                        modifier = Modifier.background(if (isSelected) Color(0xFFEAF1FF) else Color.Transparent, RoundedCornerShape(14.dp))
                    )
                }
            }
        }
    }
}

@Composable
private fun ShellAccountMenu(
    expanded: Boolean,
    onDismiss: () -> Unit,
    profileName: String,
    profileRole: String,
    profileEmail: String,
    profileImage: ImageBitmap?,
    onMyProfile: () -> Unit,
    actionScope: CoroutineScope,
    onChangeRole: () -> Unit,
    onLogout: () -> Unit
) {
    AppDropdownMenu(
        expanded = expanded,
        onDismissRequest = onDismiss,
        offset = DpOffset(0.dp, 8.dp),
        shape = RoundedCornerShape(18.dp),
        containerColor = Color.White,
        tonalElevation = 0.dp,
        shadowElevation = 20.dp,
        modifier = Modifier
            .width(232.dp)
            .border(1.dp, ShellBorder, RoundedCornerShape(18.dp))
    ) {
        Column(modifier = Modifier.padding(vertical = 8.dp)) {
            Row(
                modifier = Modifier.padding(horizontal = 16.dp, vertical = 10.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                AvatarChip(initials = shellAvatarInitials(profileName), image = profileImage, compact = true)
                Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
                    Text(profileName.ifBlank { "User" }, fontFamily = interBold(), color = ShellNavy, maxLines = 1, overflow = TextOverflow.Ellipsis)
                    Text(profileRole, color = ShellNavy.copy(alpha = 0.72f), style = MaterialTheme.typography.bodySmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
                    if (profileEmail.isNotBlank()) {
                        Text(profileEmail, color = ShellNavy.copy(alpha = 0.72f), style = MaterialTheme.typography.bodySmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
                    }
                }
            }
            DropdownMenuItem(
                text = { Text("My Profile", fontFamily = interSemiBold(), color = ShellNavy) },
                leadingIcon = {
                    Icon(painter = painterResource(Res.drawable.user), contentDescription = null, tint = ShellBlue, modifier = Modifier.size(20.dp))
                },
                onClick = {
                    onMyProfile()
                    actionScope.launch {
                        delay(1)
                        onDismiss()
                    }
                }
            )
        DropdownMenuItem(
            text = {
                Text(
                    text = "Change Role",
                    fontFamily = interSemiBold(),
                    color = ShellNavy
                )
            },
            leadingIcon = {
                Icon(
                    painter = painterResource(Res.drawable.ic_rotate_right),
                    contentDescription = null,
                    tint = ShellBlue,
                    modifier = Modifier.size(20.dp)
                )
            },
            onClick = {
                onDismiss()
                onChangeRole()
            }
        )
        DropdownMenuItem(
            text = {
                Text(
                    text = "Sign Out",
                    fontFamily = interSemiBold(),
                    color = ShellNavy
                )
            },
            leadingIcon = {
                Icon(
                    painter = painterResource(Res.drawable.logout),
                    contentDescription = null,
                    tint = ShellBlue,
                    modifier = Modifier.size(20.dp)
                )
            },
            onClick = {
                onDismiss()
                onLogout()
            }
        )
        }
    }
}

@Composable
private fun AvatarChip(
    initials: String,
    image: ImageBitmap? = null,
    compact: Boolean,
    modifier: Modifier = Modifier
) {
        Box(
            modifier = modifier
                .size(if (compact) 38.dp else 48.dp)
                .clip(CircleShape)
                .background(ShellBlue, CircleShape),
            contentAlignment = Alignment.Center
    ) {
        if (image != null) {
            Image(
                bitmap = image,
                contentDescription = "Profile photo",
                modifier = Modifier.fillMaxSize(),
                contentScale = ContentScale.Crop
            )
        } else {
            Text(
                text = initials.uppercase(),
                color = Color.White,
                style = if (compact) MaterialTheme.typography.titleSmall else MaterialTheme.typography.titleMedium,
                fontFamily = interBold()
            )
        }
    }
}
